import Neutralino from "@neutralinojs/lib";
import { useEffect, useState } from "react";
import { useIsDesktop } from "./useIsDesktop";

interface NeutralinoType {
  os: {
    open: (url: string) => Promise<void>;
  };
}

export const useOpenUrl = () => {
  const isDesktop = useIsDesktop();
  const [neutralinoReady, setNeutralinoReady] = useState(false);

  useEffect(() => {
    if (!isDesktop) {
      return;
    }

    // Check if Neutralino is already ready
    if (window.__NEUTRALINO_READY__) {
      setNeutralinoReady(true);
      return;
    }

    // Listen for Neutralino ready event
    const handleNeutralinoReady = () => {
      console.log("📡 useOpenUrl: Neutralino ready event received");
      setNeutralinoReady(true);
    };

    window.addEventListener("neutralino-ready", handleNeutralinoReady);

    return () => {
      window.removeEventListener("neutralino-ready", handleNeutralinoReady);
    };
  }, [isDesktop]);

  return async (url: string) => {
    if (isDesktop && neutralinoReady) {
      // Try window.Neutralino first, then fall back to imported Neutralino
      const neutralinoApi = window.Neutralino || Neutralino;

      if (neutralinoApi) {
        try {
          const neu = neutralinoApi as NeutralinoType;
          const _apiSource = window.Neutralino
            ? "window.Neutralino"
            : "imported Neutralino";
          await neu.os.open(url);
          return;
        } catch (error) {
          console.error("❌ Error calling Neutralino.os.open:", error);
          // Fall back to regular window.open if Neutralino fails
        }
      } else {
        console.warn(
          "❌ Neither window.Neutralino nor imported Neutralino available",
        );
      }
    } else if (isDesktop && !neutralinoReady) {
      console.log(
        "⏳ Desktop mode detected but Neutralino not ready yet - falling back to window.open",
      );
    } else {
      console.log("❌ Desktop mode NOT detected - will use window.open");
    }

    // Fallback to regular window.open
    window.open(url, "_blank");
  };
};
